import { useState, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Hook for SSE-streamed AI chat. Returns token-by-token text as it arrives.
 * Falls back to the non-streaming contextChat endpoint if SSE fails.
 */
export function useStreamChat(patientId, role = 'patient') {
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef(null);

  const sendMessage = useCallback(async (text) => {
    if (!text?.trim() || isStreaming) return;

    const userMsg = { role: 'user', content: text.trim(), timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setIsStreaming(true);

    // Add placeholder for assistant message
    const assistantIdx = { current: -1 };
    setMessages(prev => {
      assistantIdx.current = prev.length;
      return [...prev, { role: 'assistant', content: '', timestamp: new Date().toISOString(), streaming: true }];
    });

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      const token = localStorage.getItem('auth_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const activeSiteId = localStorage.getItem('emr_active_site_id');
      if (activeSiteId) headers['X-Site-Id'] = activeSiteId;
      const activeTenantId = localStorage.getItem('emr_active_tenant_id');
      if (activeTenantId) headers['X-Tenant-Id'] = activeTenantId;

      const response = await fetch(`${API_URL}/api/v1/patient-ai/patient/${patientId}/stream-chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: text.trim(),
          conversationId,
          role,
        }),
        signal: controller.signal,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let metaReceived = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            const eventType = line.slice(7).trim();
            // Next line should be data
            continue;
          }
          if (line.startsWith('data: ')) {
            const rawData = line.slice(6);
            try {
              const data = JSON.parse(rawData);

              // Determine event type from the previous event: line
              if (data.conversation_id && !metaReceived) {
                // meta event
                setConversationId(data.conversation_id);
                metaReceived = true;
              } else if (data.text !== undefined) {
                // token event — append to assistant message
                setMessages(prev => {
                  const updated = [...prev];
                  const idx = assistantIdx.current;
                  if (idx >= 0 && updated[idx]) {
                    updated[idx] = { ...updated[idx], content: updated[idx].content + data.text };
                  }
                  return updated;
                });
              } else if (data.provider !== undefined && data.conversation_id) {
                // done event
                setConversationId(data.conversation_id);
                setMessages(prev => {
                  const updated = [...prev];
                  const idx = assistantIdx.current;
                  if (idx >= 0 && updated[idx]) {
                    updated[idx] = { ...updated[idx], streaming: false, provider: data.provider };
                  }
                  return updated;
                });
              } else if (data.error) {
                // error event
                setMessages(prev => {
                  const updated = [...prev];
                  const idx = assistantIdx.current;
                  if (idx >= 0 && updated[idx]) {
                    updated[idx] = { ...updated[idx], content: data.error, streaming: false, isError: true };
                  }
                  return updated;
                });
              }
            } catch {
              // ignore malformed JSON
            }
          }
        }
      }

      // Ensure streaming flag is cleared
      setMessages(prev => {
        const updated = [...prev];
        const idx = assistantIdx.current;
        if (idx >= 0 && updated[idx]?.streaming) {
          updated[idx] = { ...updated[idx], streaming: false };
        }
        return updated;
      });
    } catch (err) {
      if (err.name === 'AbortError') return;

      // Fallback: non-streaming context-chat
      try {
        const { contextChat } = await import('@/services/patientWorkflowService');
        const res = await contextChat(patientId, text.trim(), role, conversationId);
        setConversationId(res.conversation_id);
        setMessages(prev => {
          const updated = [...prev];
          const idx = assistantIdx.current;
          if (idx >= 0 && updated[idx]) {
            updated[idx] = {
              ...updated[idx],
              content: res.response,
              streaming: false,
              provider: res.provider,
            };
          }
          return updated;
        });
      } catch {
        setMessages(prev => {
          const updated = [...prev];
          const idx = assistantIdx.current;
          if (idx >= 0 && updated[idx]) {
            updated[idx] = {
              ...updated[idx],
              content: 'Sorry, I encountered an error. Please try again.',
              streaming: false,
              isError: true,
            };
          }
          return updated;
        });
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [patientId, role, conversationId, isStreaming]);

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
  }, []);

  const newConversation = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setConversationId(null);
    setIsStreaming(false);
  }, []);

  const loadConversation = useCallback(async (convId) => {
    try {
      const { getConversation } = await import('@/services/patientWorkflowService');
      const conv = await getConversation(patientId, convId);
      setConversationId(convId);
      setMessages(
        (conv.messages || []).map(m => ({
          role: m.role,
          content: m.content,
          timestamp: m.timestamp,
        }))
      );
    } catch {
      // ignore
    }
  }, [patientId]);

  return {
    messages,
    conversationId,
    isStreaming,
    sendMessage,
    stopStreaming,
    newConversation,
    loadConversation,
    setMessages,
  };
}
